import React, { useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FileIcon, DefaultExtensionType } from "react-file-icon";
import { useTheme, lighten } from "@mui/material/styles";
import { Typography, Grid, Badge, TableRow, TableCell, Tooltip, Button } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faEllipsis,
  faEllipsisH,
  faEllipsisV,
  faExclamationCircle,
  faFolderClosed,
  faSquare,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";

import CustomTablePagination from "../../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";

import { handleDownloadFile } from "../../../../services/documents/document-files-api.service";

import { getItemPeriodValue } from "../../../../models/documents/period";

import { Props, State } from "./interfaces";
import {
  DocumentContainer,
  VerfiyDocumentButtonText,
  DocumentActionsContainer,
  AttachedDocumentContainer,
  FilesContainer,
  StyledFontAwesomeIcon,
} from "../../styles";
import { getFileExtension } from "../../../../utils/files-utils/get-file-extension";
import { READABLE_FILE_TYPES, READABLE_IMAGE_TYPES } from "../../../../reusable-components/DocumentViewer/fileTypes";
import { IconButton } from "../../../AttachedDocumentSummary/styles";
import { intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import { fileSize } from "../../../Document/NewFile/utils";
import { customFileIconStyles } from "../../../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import { DocumentViewState } from "../../../Document/DocumentHeader/interfaces";
import SimplePopover from "../../../../reusable-components/SimplePopover/SimplePopover";
import { Popover } from "../../../../reusable-components/SimplePopover/styles";
import DocumentViewer from "../../../../reusable-components/DocumentViewer/DocumentViewer";
import { EmptyFileIcon, FolderIcon } from "../../../../assets/icons";
import AttachedDocuments from "../../../AttachedDocuments/AttachedDocuments";
import AddNewDocument from "../../../AddNewDocument/AddNewDocument";
import { DOCUMENT_CATEGORIES, DOCUMENTS_STATUS_TYPES } from "../../../../utils/constants/constants";
import { DocumentsApiService } from "../../../../services/documents";
import { DocumentSTO } from "../../../../models/documents";
import CustomConfirmationChangesDialog from "../../../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import useAuthorization from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS, ACCESS_TYPES } from "../../../../utils/constants/authorization-constants";
import { EmployeeDataContext } from "../../EmployeePersonalAndChangingDocuments";
import { DocumentCategoryRef } from "../../../../models/document-categories";

const { ProcessAccounting, ProcessIncomingDocuments } = AUTHORIZATIONS.LegalEntityAccess;

const PersonalDocument = ({ document, documentType, handleNotify }: Props) => {
  const { i18n, t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP -- translation root path
  const theme = useTheme();
  const { employee, forManager, isReadOnly } = useContext(EmployeeDataContext);

  const [documentViewState, setDocumentViewState] = useState<DocumentViewState>({
    isViewing: false,
    fileDto: null,
  });

  const allowMarkFileAsVerified = useAuthorization([
    ProcessAccounting,
    ProcessIncomingDocuments,
    ACCESS_TYPES.CHANGE_ALL.id,
  ]);

  const [state, setState] = useState<State>({
    isCCCDOpen: false,
    CCCDText: "",
    documentToVerify: null,
    loading: false,
  });

  const markDocumentAsVerified = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new DocumentsApiService().saveItems([
        {
          ...state.documentToVerify,
          status: DOCUMENTS_STATUS_TYPES.VERIFIED,
        } as DocumentSTO,
      ]);
      if (!response.error) {
        setState((state) => ({
          ...state,
          error: "",
          documentToVerify: null,
          isCCCDOpen: false,
          CCCDText: "",
        }));
        !!handleNotify && handleNotify();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.documentToVerify]);
  return (
    <>
      <TableRow>
        <TableCell style={{ padding: "10px" }}>
          <Grid style={{ display: "flex", justifyContent: "space-between" }}>
            <Grid style={{ width: "max-content" }}>
              {`${!forManager ? t(`${tRP}.common_keys.my_prefix`) : ""} ${documentType?.name}`}
              {!document && (
                <Typography
                  style={{ color: theme.palette.error.main }}
                  variant="body2"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  {`${t(`${tRP}.employee_documents.missing_file`)}`}
                </Typography>
              )}
            </Grid>
            {!isReadOnly && documentType && (
              <AddNewDocument
                buttonType="plusButton"
                handleNotifyParent={handleNotify}
                documentFieldsProps={{
                  category: documentType.category,
                  legalEntity: employee?.legalEntity,
                  type: documentType,
                  employee: employee,
                }}
                readOnlyFields={{
                  isCategoryRO: true,
                  isLegalEntityRO: true,
                  isTypeRO: true,
                  isEmployeeRO: true,
                }}
                hideReadOnlyFields
                useMiniPlusButton={true}
              />
            )}
          </Grid>
        </TableCell>

        <TableCell>
          <Grid style={{ display: "flex" }}>
            {document && (
              <DocumentContainer
                key={document.id}
                forManager={forManager}
              >
                {document?.files.length > 0 ? (
                  <FilesContainer>
                    {document.files.map((file) => {
                      const fileExtension = getFileExtension(file.name).toLowerCase();
                      return (
                        <Grid
                          key={file.id}
                          title={`"${file.name}" ${intlFormatDateWithHour(file.createdAt)}`}
                        >
                          {!READABLE_FILE_TYPES.includes(fileExtension) &&
                            !READABLE_IMAGE_TYPES.includes(fileExtension) && (
                              <IconButton
                                title={`"${file.name}" ${fileSize(file.size)} } ${intlFormatDateWithHour(
                                  file.createdAt
                                )}`}
                                onClick={() => handleDownloadFile(file)}
                              >
                                <FileIcon
                                  extension={fileExtension}
                                  {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                                  radius={3}
                                />
                              </IconButton>
                            )}

                          {(READABLE_FILE_TYPES.includes(fileExtension) ||
                            READABLE_IMAGE_TYPES.includes(fileExtension)) && (
                            <IconButton
                              onClick={() => {
                                setDocumentViewState((documentViewState) => ({
                                  ...documentViewState,
                                  isViewing: true,
                                  fileDto: file,
                                }));
                              }}
                            >
                              <FileIcon
                                extension={fileExtension}
                                {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                                radius={3}
                              />
                            </IconButton>
                          )}
                        </Grid>
                      );
                    })}
                    {/* {document.files.length > 3 && (
                      <Badge
                        color={"primary"}
                        showZero
                        badgeContent={`+${document.files.length - 3}`}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        title={"More files..."}
                      >
                        <SimplePopover
                          buttonType="icon"
                          buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
                          flow="row"
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                          }}
                        >
                          <Grid style={{ display: "flex", gap: "3px", padding: "10px" }}>
                            {document.files.slice(3).map((file) => {
                              const fileExtension = getFileExtension(file.name);

                              return (
                                <Grid
                                  key={file.id}
                                  title={`"${file.name}" ${intlFormatDateWithHour(file.createdAt)}`}
                                >
                                  {!READABLE_FILE_TYPES.includes(fileExtension) &&
                                    !READABLE_IMAGE_TYPES.includes(fileExtension) && (
                                      <IconButton
                                        title={`"${file.name}" ${intlFormatDateWithHour(file.createdAt)}`}
                                        onClick={() => handleDownloadFile(file)}
                                      >
                                        <FileIcon
                                          extension={fileExtension}
                                          {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                                          radius={3}
                                        />
                                      </IconButton>
                                    )}

                                  {(READABLE_FILE_TYPES.includes(fileExtension) ||
                                    READABLE_IMAGE_TYPES.includes(fileExtension)) && (
                                    <IconButton
                                      onClick={() => {
                                        setDocumentViewState((documentViewState) => ({
                                          ...documentViewState,
                                          isViewing: true,
                                          fileDto: file,
                                        }));
                                      }}
                                    >
                                      <FileIcon
                                        extension={fileExtension}
                                        {...customFileIconStyles[fileExtension as DefaultExtensionType]}
                                        radius={3}
                                      />
                                    </IconButton>
                                  )}
                                </Grid>
                              );
                            })}
                          </Grid>
                        </SimplePopover>
                      </Badge>
                    )} */}
                  </FilesContainer>
                ) : (
                  <Grid style={{ opacity: "0.2", padding: "7px" }}>
                    <Tooltip title={`${t(`${tRP}.employee_documents.empty_document`)}`}>
                      <EmptyFileIcon />
                    </Tooltip>
                  </Grid>
                )}

                {document?.type?.dateInsteadPeriod ? (
                  <Typography sx={{ fontWeight: 600 }}>
                    {intlFormatDateShort(document?.documentDate ?? new Date())}
                  </Typography>
                ) : (
                  <Typography sx={{ fontWeight: 600 }}>{getItemPeriodValue(document?.period)}</Typography>
                )}
                {forManager && (
                  <AttachedDocumentContainer dir={i18n.dir()}>
                    <SimplePopover
                      buttonType="icon"
                      buttonContent={
                        <FontAwesomeIcon
                          icon={faEllipsisV}
                          title={"Document actions"}
                          style={{ padding: "0", minWidth: "unset", minHeight: "unset" }}
                        />
                      }
                    >
                      <DocumentActionsContainer>
                        {!isReadOnly && (
                          <Button
                            style={{
                              padding: "0",
                              justifyContent: "flex-start",
                            }}
                            onClick={() =>
                              setState(() => ({
                                ...state,
                                isCCCDOpen: true,
                                CCCDText: `${t(`${tRP}.employee_documents.confirm_mark_as_verified_message`)}`,
                                documentToVerify: document,
                              }))
                            }
                            disabled={
                              document.status?.id === DOCUMENTS_STATUS_TYPES.VERIFIED.id || !allowMarkFileAsVerified
                            }
                          >
                            <VerfiyDocumentButtonText>{`${t(
                              `${tRP}.employee_documents.verify_document`
                            )}`}</VerfiyDocumentButtonText>
                          </Button>
                        )}
                        <AttachedDocuments
                          isWidget
                          hideReadOnlyFields
                          documentIDs={[document?.id]}
                          count={document.filesCount}
                          closeAfterDocumentDelete={true}
                          handleNotifyParent={handleNotify}
                          showAsLink={true}
                          linkText={`${t(`${tRP}.employee_documents.view_document`)}`}
                          linkStyle={{
                            textDecoration: "none",
                            borderBottom: "none",
                            fontSize: theme.typography.body1.fontSize,
                          }}
                          useSimpleView
                          documentIsReadOnly={isReadOnly}
                        />
                      </DocumentActionsContainer>
                    </SimplePopover>
                  </AttachedDocumentContainer>
                )}
              </DocumentContainer>
            )}

            {!!documentViewState.fileDto && (
              <Popover
                open={documentViewState.isViewing}
                anchorPosition={{ left: 0, top: 0 }}
                anchorReference={"anchorPosition"}
              >
                <IconButton
                  onClick={() => {
                    setDocumentViewState({
                      ...documentViewState,
                      isViewing: false,
                      fileDto: null,
                    });
                  }}
                >
                  <StyledFontAwesomeIcon icon={faClose} />
                </IconButton>
                {documentViewState.fileDto && (
                  <DocumentViewer
                    fileDto={documentViewState.fileDto}
                    bypasAllowDownload
                  />
                )}
              </Popover>
            )}
          </Grid>
        </TableCell>
      </TableRow>
      <CustomConfirmationChangesDialog
        open={state.isCCCDOpen}
        loading={state.loading}
        title={`${t(`${tRP}.employee_documents.confirm_mark_as_verified_title`)}`}
        actionsAndHandlers={[
          {
            action: `${t(`buttons.confirm`)}`,
            handler: markDocumentAsVerified,
            buttonVariant: "contained",
          },
        ]}
        handleCancel={() => setState((state) => ({ ...state, isCCCDOpen: false }))}
      >
        {state.CCCDText}
      </CustomConfirmationChangesDialog>
    </>
  );
};

export default PersonalDocument;
